import { useMemo } from 'react';

/**
 * Shared client-side pagination math used by the report/list pages
 * (Mood, Daily Attendance Logs, Random Attendance Logs, …).
 *
 * Computes EXACTLY the three lines each page inlined:
 *   totalPages    = Math.max(1, Math.ceil(items.length / pageSize));
 *   safePage      = Math.min(currentPage, totalPages);
 *   paginatedData = useMemo(() => items.slice((safePage-1)*pageSize, safePage*pageSize), …);
 *
 * Behaviour-preserving: `safePage` clamps an over-large `currentPage` down to
 * the last page, and `paginatedData` is memoised on `[items, safePage, pageSize]`
 * so the slice only recomputes when those change.
 */
interface UsePaginationResult<T> {
  totalPages: number;
  safePage: number;
  paginatedData: T[];
}

export function usePagination<T>(
  items: T[],
  currentPage: number,
  pageSize: number,
): UsePaginationResult<T> {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize],
  );

  return { totalPages, safePage, paginatedData };
}
